import { resolve } from "path";

export type WakeSignalWorkerRuntimeDiagnostics = {
  status: string;
  provider: string | null;
  intervalMs: number | null;
  batchSize: number | null;
  maxAttempts: number | null;
  startedAt: string | null;
  stoppedAt: string | null;
  lastDrainAt: string | null;
  lastError: string | null;
  lastResult: Record<string, number> | null;
};

export type WakeSignalWorkersDiagnosticsSnapshot = Record<string, WakeSignalWorkerRuntimeDiagnostics>;

const WORKER_NAME_PATTERN = /^[a-z][a-z0-9_:-]{0,63}$/;
const URL_PATTERN = /https?:\/\/[^\s"'<>]+/g;
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const SECRET_ASSIGNMENT_PATTERN = /\b(token|secret|password|api[_-]?key|authorization)=([^\s"'<>]+)/gi;

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumberOrNull(value: unknown): number | null {
  const numberValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

export function redactDiagnosticText(value: string | null): string | null {
  if (!value) return null;
  return value
    .replace(SECRET_ASSIGNMENT_PATTERN, "$1=[redacted]")
    .replace(URL_PATTERN, "[redacted-url]")
    .replace(EMAIL_PATTERN, "[redacted-email]")
    .slice(0, 240);
}

function normalizeDrainResult(value: unknown): Record<string, number> | null {
  if (!isObject(value)) return null;
  const result: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (!/^[a-z][a-z0-9_:-]{0,63}$/.test(key)) continue;
    const numberValue = asNumberOrNull(raw);
    if (numberValue !== null) result[key] = numberValue;
  }
  return Object.keys(result).length > 0 ? result : null;
}

export function normalizeWakeSignalWorkerRuntimeDiagnostics(
  value: unknown,
): WakeSignalWorkerRuntimeDiagnostics | null {
  if (!isObject(value)) return null;
  return {
    status: asString(value.status) ?? "unknown",
    provider: asString(value.provider),
    intervalMs: asNumberOrNull(value.intervalMs),
    batchSize: asNumberOrNull(value.batchSize),
    maxAttempts: asNumberOrNull(value.maxAttempts),
    startedAt: asString(value.startedAt),
    stoppedAt: asString(value.stoppedAt),
    lastDrainAt: asString(value.lastDrainAt),
    lastError: redactDiagnosticText(asString(value.lastError)),
    lastResult: normalizeDrainResult(value.lastResult),
  };
}

export function normalizeWakeSignalWorkerDiagnostics(input: unknown): WakeSignalWorkersDiagnosticsSnapshot {
  if (!isObject(input)) return {};
  const workers: WakeSignalWorkersDiagnosticsSnapshot = {};
  for (const [workerName, value] of Object.entries(input)) {
    if (!WORKER_NAME_PATTERN.test(workerName)) continue;
    const diagnostics = normalizeWakeSignalWorkerRuntimeDiagnostics(value);
    if (diagnostics) workers[workerName] = diagnostics;
  }
  return workers;
}

async function readPlatformWorkerRuntimeState(params: {
  functionsPath: string;
  fileName: string;
  getterName: string;
  provider: string;
}): Promise<unknown> {
  try {
    const mod = await import(resolve(params.functionsPath, params.fileName));
    const getter = (mod as Record<string, unknown>)[params.getterName];
    if (typeof getter !== "function") {
      return {
        status: "error",
        provider: params.provider,
        lastError: `worker diagnostics getter missing: ${params.getterName}`,
      };
    }
    return getter();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      status: "error",
      provider: params.provider,
      lastError: message,
    };
  }
}

export async function getPlatformWorkerDiagnostics(params: {
  functionsPath: string;
}): Promise<WakeSignalWorkersDiagnosticsSnapshot> {
  const [email, webhook] = await Promise.all([
    readPlatformWorkerRuntimeState({
      functionsPath: params.functionsPath,
      fileName: "email-worker.ts",
      getterName: "getPlatformEmailWorkerRuntimeState",
      provider: "resend",
    }),
    readPlatformWorkerRuntimeState({
      functionsPath: params.functionsPath,
      fileName: "webhook-worker.ts",
      getterName: "getPlatformWebhookWorkerRuntimeState",
      provider: "http",
    }),
  ]);
  return normalizeWakeSignalWorkerDiagnostics({ email, webhook });
}
